import { selectQuotePrice } from '@ui/features/quote-builder/state/selectors/quote-price';
import { snapSiteConfigSelectors } from '@ui/features/snap-site-config/state';
import { createAsyncThunk } from '@ui/store/create-async-thunk';

import { onPartQuotePriceCreation } from '../calculations/recalculate/on-part-quote-price-creation';
import { createSnapQuotePrice } from '../endpoints/create-snap-quote-price';
import { actions } from '../reducer';
import { selectQuoteBuilder } from '../selectors/quote-builder';
import { selectQuoteLineWithLeadTimeIssues } from '../selectors/quote-line';

/**
 * Create a quote price optimistically, updating the state immediately.
 *
 * @remarks
 *
 * When given a `quotePriceUuid` (the quote price exists in the store state but
 * the server sync failed), it will use the existing quote price as a base for
 * the optimistic creation. If not given, it will create a new quote price using
 * the default logic.
 *
 * Validates that the given quote price is in the 'creating' or
 * 'creation-failed' state, otherwise it isn't sensible to optimistically create
 * it.
 */
export const createQuotePriceOptimistically = createAsyncThunk<
  void,
  { quoteLineUuid: string; quotePriceUuid?: string },
  {
    fulfilledMeta: { quoteLineUuid: string; quotePriceUuid: string };
    rejectedMeta: { quoteLineUuid: string; quotePriceUuid: string };
    rejectValue: string;
  }
>(
  'quoteBuilder/createQuotePriceOptimistically',
  async (
    { quoteLineUuid, quotePriceUuid },
    { getState, dispatch, fulfillWithValue, rejectWithValue },
  ) => {
    const state = getState();
    const quoteBuilder = selectQuoteBuilder(state);
    const quoteLine = selectQuoteLineWithLeadTimeIssues(state, quoteLineUuid);
    const priceStrategyConfigs =
      snapSiteConfigSelectors.selectSnapSiteConfig(state).priceStrategies;

    const quotePrice = quotePriceUuid
      ? selectQuotePrice(state, quoteLineUuid, quotePriceUuid)
      : onPartQuotePriceCreation(quoteBuilder, quoteLine, priceStrategyConfigs);

    if (!quotePrice) {
      const errorMessage = 'Failed to create quote price';

      console.error(errorMessage);

      return rejectWithValue(errorMessage, {
        quoteLineUuid,
        quotePriceUuid: quotePriceUuid ?? 'unknown',
      });
    }

    if (
      quotePrice.status !== 'creating' &&
      quotePrice.status !== 'creation-failed'
    ) {
      return rejectWithValue(
        `Cannot optimistically create quote price with status ${quotePrice.status}`,
        {
          quoteLineUuid,
          quotePriceUuid: quotePrice.uuid,
        },
      );
    }

    /**
     * @NOTE(shawk): Important so that quote prices with the `creation-failed`
     * states are shown as creating when we retry.
     */
    quotePrice.status = 'creating';

    dispatch(
      actions.quotePriceCreated({
        quoteLineUuid,
        quotePrice,
      }),
    );

    try {
      await dispatch(
        createSnapQuotePrice.initiate({
          params: {
            siteCode: quoteBuilder.siteCode,
            snapQuoteUuid: quoteBuilder.uuid,
            snapQuoteLineUuid: quoteLineUuid,
          },
          body: quotePrice,
        }),
      ).unwrap();

      return fulfillWithValue(undefined, {
        quoteLineUuid,
        quotePriceUuid: quotePrice.uuid,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create quote price';

      return rejectWithValue(errorMessage, {
        quoteLineUuid,
        quotePriceUuid: quotePrice.uuid,
      });
    }
  },
);
